<template>
  <div class="doc-more-menu">
    <div
      ref="menuButton"
      class="doc-more-menu__button-wrapper"
      @click.stop.prevent="openMenu"
    >
      <slot>
        <button class="doc-more-menu__button" aria-label="More actions">
          <span
            class="doc-more-menu__button-icon iconfont imore"
            aria-hidden="true"
          />
        </button>
      </slot>
    </div>

    <!-- Use Teleport so the cards' CSS doesn't affect this menu. -->
    <Teleport v-if="hasOpened" to="#appRoot">
      <BasePopup
        ref="menu"
        class="doc-more-menu__popup"
        menu
        :anchor="menuButton"
      >
        <div class="doc-more-menu__list">
          <button class="doc-more-menu__item" @click="openDestinationMenu">
            <span
              class="doc-more-menu__icon iconfont"
              :class="listsView.isDocInAnyList ? 'iflag2' : 'iflag'"
              aria-hidden="true"
            />
            {{ listsView.isDocInAnyList ? 'Saved' : 'Save' }}
          </button>
          <button
            v-if="canMarkFinished(doc)"
            class="doc-more-menu__item"
            :disabled="isBusy"
            @click="runAction(markDocFinishedWithUndo)"
          >
            <span
              class="doc-more-menu__icon iconfont icheck-bold"
              aria-hidden="true"
            />
            Mark as Finished
          </button>
          <router-link
            v-if="canDownload(doc) && !hideDownload"
            class="doc-more-menu__item"
            :to="{
              name: docRouteNames[doc.doc_type].export,
              params: { url_slug: doc.url_slug },
            }"
            @click="menu?.hide()"
          >
            <span
              class="doc-more-menu__icon iconfont idownload"
              aria-hidden="true"
            />
            Download
          </router-link>
          <button
            v-if="canMarkNotStarted(doc)"
            class="doc-more-menu__item"
            :disabled="isBusy"
            @click="runAction(markDocNotStartedWithUndo)"
          >
            <span
              class="doc-more-menu__icon iconfont ireset"
              aria-hidden="true"
            />
            Mark as Not Started
          </button>
          <a
            v-if="isSingleDocPage && doc.amazon_link && !isAndroidDevice"
            class="doc-more-menu__item"
            :href="doc.amazon_link"
            target="_blank"
            @click="menu?.hide()"
          >
            <span
              class="doc-more-menu__icon iconfont icart-buy"
              aria-hidden="true"
            />
            Buy on Amazon
          </a>
        </div>
      </BasePopup>

      <AddToListPopup
        ref="destinationMenu"
        :view="listsView"
        :newStyle20321B="true"
        :anchor="menuButton"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import type { Raw } from 'vue'

import { useWait } from '@/app/composables/useWait'
import {
  canDownload,
  canMarkFinished,
  canMarkNotStarted,
  docRouteNames,
  DocView,
} from '@/models/doc'
import type { DocsView } from '@/models/doc'
import { DocCustomListsView } from '@/models/doc.custom.lists'
import {
  markDocFinishedWithUndo,
  markDocNotStartedWithUndo,
} from '@/models/doc.read.state'
import type { UserDocListItem } from '@/models/interfaces'
import { platform } from '@/services/platform'
import { Sentry } from '@/services/sentry'

import BasePopup from '@/components/ui/BasePopup.vue'
import AddToListPopup from '@/app/components/doc/AddToListPopup.vue'

/**
 * The doc's "more" menu with the Save destination menu and reading state
 * actions. It replaces `DocLibraryMenu` and the lists menus.
 *
 * Like `DocLibraryMenu`, it can be used in a doc listing page by passing the
 * `docsView` prop, or in a single doc page, by passing a `DocView` instance
 * to the `doc` prop.
 */
const props = defineProps<{
  /**
   * The doc that we take action upon.
   */
  doc: UserDocListItem | DocView
  /**
   * The listing page's view. Required when `doc` isn't a `DocView`.
   */
  docsView?: DocsView
  /**
   * Hide the Download row, for pages that already show a Download button.
   */
  hideDownload?: boolean
}>()

const isSingleDocPage = props.doc instanceof DocView
if (!isSingleDocPage && !props.docsView) {
  throw new Error(
    'Incorrect usage of DocMoreMenu. ' +
      'If `doc` is not an instance of `DocView`, ' +
      'then `docsView` must not be undefined.',
  )
}

const menuButton = ref<HTMLElement | null>(null)
const menu = ref<InstanceType<typeof BasePopup> | null>(null)
const destinationMenu = ref<InstanceType<typeof AddToListPopup> | null>(null)
const wait = useWait()
const listsView = ref<Raw<DocCustomListsView>>(
  new DocCustomListsView(props.doc, wait, props.docsView),
)
// The reading page replaces the DocView when a different guide is opened.
// This component stays mounted, so update here with the listsView too.
watch(
  () => props.doc,
  (doc) => {
    listsView.value = new DocCustomListsView(doc, wait, props.docsView)
  },
)
const isBusy = ref(false)

// Mount the popup when the menu is first opened.
const hasOpened = ref(false)

// We use this to hide the Amazon link on Android (see `DocLibraryMenu`). The
// link is only shown on a single doc page.
const isAndroidDevice = ref(false)
onMounted(async () => {
  if (isSingleDocPage) {
    isAndroidDevice.value = await platform.isNativeAndroidApp()
  }
})

async function openMenu(): Promise<void> {
  hasOpened.value = true
  // Wait for the popup to mount before showing it.
  await nextTick()
  menu.value?.show()
}

async function openDestinationMenu(): Promise<void> {
  menu.value?.hide()
  await destinationMenu.value?.show()
}

/**
 * Close the menu and run `action` on the doc, ignoring repeated clicks while
 * it's pending.
 */
async function runAction(
  action: (
    doc: UserDocListItem | DocView,
    docsView?: DocsView,
  ) => Promise<void>,
): Promise<void> {
  menu.value?.hide()
  isBusy.value = true
  try {
    await action(props.doc, props.docsView)
  } catch (error) {
    Sentry.captureException(error)
  } finally {
    isBusy.value = false
  }
}
</script>

<style lang="scss" scoped>
.doc-more-menu {
  // The popups are positioned next to this element, so constrain the wrapper
  // to only be as wide as the button.
  &__button-wrapper {
    width: fit-content;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    // Like the "more" button of `DocLibraryMenu`, the hover highlight is
    // larger than the icon, but the negative margin keeps the button's layout
    // at 28px, the size of the Save button.
    width: 40px;
    height: 40px;
    margin: -6px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: none;
    color: $c-black-3;
    cursor: pointer;

    @include hover {
      background: rgba($c-black-2, 0.05);
    }
  }

  &__button-icon {
    font-size: 24px;
    line-height: 1;
  }

  &__list {
    display: flex;
    flex-direction: column;
    // Extend the rows 8px on each side, so that the hover highlight surrounds
    // the icon, as in the destination menu.
    margin-inline: -8px;
  }

  &__item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 15px 8px;
    border: 0;
    border-radius: 4px;
    background: none;
    color: $c-black-15;
    font-size: 16px;
    text-align: start;
    cursor: pointer;

    @include hover {
      background: $c-light-grey;
    }

    &:disabled {
      cursor: default;
      opacity: 0.7;
    }

    // Like the destination menu, the divider between each row is only as long
    // as the content, and doesn't extend the 8px on each side that we add
    // above.
    &:not(:last-child):after {
      content: '';
      position: absolute;
      right: 8px;
      bottom: 0;
      left: 8px;
      height: 1px;
      background: $c-black-17;
    }
  }

  &__icon {
    font-size: 20px;
  }
}

.night-mode .doc-more-menu {
  &__button {
    color: $c-dark-4;

    @include hover {
      background: rgba($c-dark-3, 0.5);
    }
  }

  &__item {
    color: $c-dark-4;

    @include hover {
      background: $c-dark-2;
    }

    &:not(:last-child):after {
      background: $c-dark-5;
    }
  }
}
</style>
